import { useEffect, useState, useMemo } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import {
  ConcentricCircles,
  PlasticCumulativeUsers,
  PlasticCumulativeDroppers,
  PlasticConversions,
  PlasticCumulativeNested,
} from "../../components";
import { usePlasticDashboard } from "../../store/usePlasticDashboard";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

function ConversionScreen() {
  const {
    cumulativeUsersData,
    getCumulativeUsers,
    isLoadingCumulativeUsers,
    cumulativeDroppersData,
    getCumulativeDroppers,
    isLoadingCumulativeDroppers,
    conversionsData,
    getConversions,
    isLoadingConversions,
    cumulativeNestedData,
    getCumulativeNested,
    isLoadingCumulativeNested,
  } = usePlasticDashboard();

  // Shared filter state for both charts
  const [period, setPeriod] = useState<
    "weekly" | "monthly" | "quarterly" | "yearly"
  >("monthly");

  // Year selector (used for quarterly views)
  const [year, setYear] = useState(new Date().getFullYear());
  // Monthly range: fromMonth/toMonth (paired with fromYear/toYear below)
  const [fromMonth, setFromMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getMonth() + 1;
  });
  const [toMonth, setToMonth] = useState(new Date().getMonth() + 1);

  // Range-based filters: fromYear/fromWeek/toYear/toWeek (shared by monthly, weekly, yearly)
  const [fromYear, setFromYear] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getFullYear();
  });
  const [fromWeek, setFromWeek] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return dayjs(d).isoWeek();
  });
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [toWeek, setToWeek] = useState(dayjs().isoWeek());

  useEffect(() => {
    if (period !== "monthly") return;
    if (fromYear > toYear) {
      setToYear(fromYear);
    }
  }, [period, fromYear, toYear]);

  useEffect(() => {
    if (period !== "monthly") return;
    if (fromYear === toYear && fromMonth > toMonth) {
      setToMonth(fromMonth);
    }
  }, [period, fromYear, toYear, fromMonth, toMonth]);

  useEffect(() => {
    if (period !== "monthly") return;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (toYear === currentYear && toMonth > currentMonth) {
      setToMonth(currentMonth);
    }
    if (fromYear === currentYear && fromMonth > currentMonth) {
      setFromMonth(currentMonth);
    }
  }, [period, fromYear, fromMonth, toYear, toMonth]);

  // Independent filters for Cumulative Nested chart
  const [nestedPeriod, setNestedPeriod] = useState<
    "weekly" | "monthly" | "quarterly" | "yearly"
  >("monthly");
  const [nestedYear, setNestedYear] = useState(new Date().getFullYear());
  const [nestedFromMonth, setNestedFromMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getMonth() + 1;
  });
  const [nestedToMonth, setNestedToMonth] = useState(new Date().getMonth() + 1);
  const [nestedFromYear, setNestedFromYear] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getFullYear();
  });
  const [nestedFromWeek, setNestedFromWeek] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return dayjs(d).isoWeek();
  });
  const [nestedToYear, setNestedToYear] = useState(new Date().getFullYear());
  const [nestedToWeek, setNestedToWeek] = useState(dayjs().isoWeek());

  useEffect(() => {
    if (nestedPeriod !== "monthly") return;
    if (nestedFromYear > nestedToYear) {
      setNestedToYear(nestedFromYear);
    }
  }, [nestedPeriod, nestedFromYear, nestedToYear]);

  useEffect(() => {
    if (nestedPeriod !== "monthly") return;
    if (nestedFromYear === nestedToYear && nestedFromMonth > nestedToMonth) {
      setNestedToMonth(nestedFromMonth);
    }
  }, [
    nestedPeriod,
    nestedFromYear,
    nestedToYear,
    nestedFromMonth,
    nestedToMonth,
  ]);

  useEffect(() => {
    if (nestedPeriod !== "monthly") return;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (nestedToYear === currentYear && nestedToMonth > currentMonth) {
      setNestedToMonth(currentMonth);
    }
    if (nestedFromYear === currentYear && nestedFromMonth > currentMonth) {
      setNestedFromMonth(currentMonth);
    }
  }, [
    nestedPeriod,
    nestedFromYear,
    nestedFromMonth,
    nestedToYear,
    nestedToMonth,
  ]);

  // Fetch data when filters change
  useEffect(() => {
    const params: any = { period };

    if (period === "weekly") {
      params.fromYear = fromYear;
      params.fromWeek = fromWeek;
      params.toYear = toYear;
      params.toWeek = toWeek;
    } else if (period === "monthly") {
      params.fromYear = fromYear;
      params.fromMonth = fromMonth;
      params.toYear = toYear;
      params.toMonth = toMonth;
    } else if (period === "quarterly") {
      params.year = year;
    } else if (period === "yearly") {
      params.fromYear = fromYear;
      params.toYear = toYear;
    }

    // Fetch shared datasets
    getCumulativeUsers(params);
    getCumulativeDroppers(params);
    getConversions(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, year, fromMonth, toMonth, fromYear, fromWeek, toYear, toWeek]);

  // Fetch nested chart with independent filters
  useEffect(() => {
    const params: any = { period: nestedPeriod };
    if (nestedPeriod === "weekly") {
      params.fromYear = nestedFromYear;
      params.fromWeek = nestedFromWeek;
      params.toYear = nestedToYear;
      params.toWeek = nestedToWeek;
    } else if (nestedPeriod === "monthly") {
      params.fromYear = nestedFromYear;
      params.fromMonth = nestedFromMonth;
      params.toYear = nestedToYear;
      params.toMonth = nestedToMonth;
    } else if (nestedPeriod === "quarterly") {
      params.year = nestedYear;
    } else if (nestedPeriod === "yearly") {
      params.fromYear = nestedFromYear;
      params.toYear = nestedToYear;
    }
    getCumulativeNested(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nestedPeriod,
    nestedYear,
    nestedFromMonth,
    nestedToMonth,
    nestedFromYear,
    nestedFromWeek,
    nestedToYear,
    nestedToWeek,
  ]);

  // Compute User Breakdown stats from last data point
  const userBreakdownStats = useMemo(() => {
    if (!cumulativeUsersData || cumulativeUsersData.length === 0) return null;
    const lastPoint = cumulativeUsersData[cumulativeUsersData.length - 1];
    return {
      allDownloads: lastPoint.cumulative_downloads || 0,
      droppers: lastPoint.cumulative_droppers || 0,
      regularDroppers: lastPoint.cumulative_5plus || 0,
    };
  }, [cumulativeUsersData]);

  // Compute Droppers Breakdown stats from last data point
  const droppersBreakdownStats = useMemo(() => {
    if (!cumulativeDroppersData || cumulativeDroppersData.length === 0)
      return null;
    const lastPoint: any =
      cumulativeDroppersData[cumulativeDroppersData.length - 1];
    return {
      totalDroppers: lastPoint.cumulative_droppers || 0,
      materialDroppers: lastPoint.cumulative_droppers_material || 0,
      plasticDroppers: lastPoint.cumulative_droppers_plastic || 0,
      activePlasticDroppers: lastPoint.cumulative_active_plastic || 0,
    };
  }, [cumulativeDroppersData]);

  return (
    <main>
      <h1 className="text-2xl font-semibold mb-4">Conversion</h1>
      {/* Definitions Section */}
      <div className="bg-white  border-gray-border drop-shadow-none rounded-xl p-4 sm:p-7 mb-8">
        <h2 className="text-xl font-semibold mb-6">Definitions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border-l-4 border-[#D2D2B2] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              DOWNLOADS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle geregistreerde app-accounts
            </p>
          </div>
          <div className="border-l-4 border-[#E3DFF7] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers die minstens{" "}
              <strong className="text-[#1F2937]">1 drop</strong> hebben gedaan
              (incl. statie)
            </p>
          </div>
          <div className="border-l-4 border-[#BFDFF7] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              MATERIAL DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers die minstens{" "}
              <strong className="text-[#1F2937]">1 materiaal drop</strong>{" "}
              hebben gedaan (dat betekent dat hier ook statie droppers bij
              zitten, zolang ze ook materiaal hebben gedropt)
            </p>
          </div>
          <div className="border-l-4 border-[#F8A991] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              PLASTIC DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers die minstens{" "}
              <strong className="text-[#1F2937]">1 plastic drop</strong> hebben
              gedaan
            </p>
          </div>
          <div className="border-l-4 border-[#19A1B9] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              STATIE ONLY DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers die{" "}
              <strong className="text-[#1F2937]">alleen statiegeld</strong>{" "}
              hebben gedropt en geen ander materiaal
            </p>
          </div>
          <div className="border-l-4 border-[#907DB1] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              ACTIVE DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers met{" "}
              <strong className="text-[#1F2937]">5 of meer</strong> drops
            </p>
          </div>
          <div className="border-l-4 border-[#FE7349] bg-[#F9FAFB] p-4 rounded-r-lg hover:shadow-md transition-shadow md:col-span-2">
            <h3 className="font-satoshi font-bold text-base mb-2 text-[#4B5563]">
              ACTIVE PLASTIC DROPPERS
            </h3>
            <p className="font-satoshi text-sm text-gray-text leading-relaxed">
              Alle gebruikers die{" "}
              <strong className="text-[#1F2937]">5 of meer</strong> drops hebben
              gedaan, én gemiddeld{" "}
              <strong className="text-[#1F2937]">10kg of meer</strong> plastic
              per jaar inleveren
            </p>
          </div>
        </div>
      </div>
      {/* Users Section */}
      <h2 className="text-xl font-semibold mt-8 mb-4">Users</h2>
      {/* Cumulative Users Chart */}
      <PlasticCumulativeUsers
        cumulativeUsersData={cumulativeUsersData}
        isLoading={isLoadingCumulativeUsers}
        period={period}
        setPeriod={setPeriod}
        year={year}
        setYear={setYear}
        fromMonth={fromMonth}
        setFromMonth={setFromMonth}
        toMonth={toMonth}
        setToMonth={setToMonth}
        fromYear={fromYear}
        setFromYear={setFromYear}
        fromWeek={fromWeek}
        setFromWeek={setFromWeek}
        toYear={toYear}
        setToYear={setToYear}
        toWeek={toWeek}
        setToWeek={setToWeek}
      />
      {/* User Breakdown */}
      {userBreakdownStats ? (
        <ConcentricCircles
          stats={[
            ...(userBreakdownStats?.allDownloads &&
            userBreakdownStats?.droppers &&
            userBreakdownStats?.regularDroppers &&
            userBreakdownStats.allDownloads >
              (userBreakdownStats.droppers +
                userBreakdownStats.regularDroppers) *
                2.5
              ? []
              : [
                  {
                    label: "Downloads (registered accounts)",
                    value: userBreakdownStats?.allDownloads || 0,
                    style: "bg-[#D2D2B2] border-2 border-[#D2D2B2]",
                    tooltip: "Total downloads by registered accounts",
                  },
                ]),
            {
              label: "Droppers",
              value: userBreakdownStats?.droppers || 0,
              style: "bg-[#E3DFF7] border-2 border-[#E3DFF7]",
              tooltip: "Total users with 1 or more drops",
            },
            {
              label: "Regular droppers",
              value: userBreakdownStats?.regularDroppers || 0,
              style: "bg-[#907DB1] border-2 border-[#907DB1]",
              tooltip: "Total users with 5+ drops",
            },
          ]}
        />
      ) : null}
      {/* Droppers Section */}
      <h2 className="text-xl font-semibold mt-8 mb-4">Droppers</h2>
      {/* Cumulative Droppers Chart */}
      <PlasticCumulativeDroppers
        cumulativeUsersData={cumulativeDroppersData}
        isLoading={isLoadingCumulativeDroppers}
        period={period}
        setPeriod={setPeriod}
        year={year}
        setYear={setYear}
        fromMonth={fromMonth}
        setFromMonth={setFromMonth}
        toMonth={toMonth}
        setToMonth={setToMonth}
        fromYear={fromYear}
        setFromYear={setFromYear}
        fromWeek={fromWeek}
        setFromWeek={setFromWeek}
        toYear={toYear}
        setToYear={setToYear}
        toWeek={toWeek}
        setToWeek={setToWeek}
      />
      {/* Droppers Breakdown */}
      {droppersBreakdownStats ? (
        <ConcentricCircles
          stats={[
            {
              label: "Total Droppers",
              value: droppersBreakdownStats?.totalDroppers || 0,
              style: "bg-[#19A1B9] border-2 border-[#19A1B9]",
              tooltip: "Total droppers",
            },
            {
              label: "Material Droppers",
              value: droppersBreakdownStats?.materialDroppers || 0,
              style: "bg-[#BFDFF7] border-2 border-[#BFDFF7]",
              tooltip: "Material droppers (excl. statie only)",
            },
            {
              label: "Plastic Droppers",
              value: droppersBreakdownStats?.plasticDroppers || 0,
              style: "bg-[#F8A991] border-2 border-[#F8A991]",
              tooltip: "Plastic droppers",
            },
            {
              label: "Active Plastic Droppers",
              value: droppersBreakdownStats?.activePlasticDroppers || 0,
              style: "bg-[#FE7349] border-2 border-[#FE7349]",
              tooltip: "Active plastic droppers",
            },
          ]}
        />
      ) : null}
      {/* User Composition Section */}
      <h2 className="text-xl font-semibold mt-8 mb-4">User Composition</h2>
      {/* Plastic Conversions Chart */}
      <PlasticConversions
        conversionsData={conversionsData}
        isLoading={isLoadingConversions}
        period={period}
        setPeriod={setPeriod}
        year={year}
        setYear={setYear}
        fromMonth={fromMonth}
        setFromMonth={setFromMonth}
        toMonth={toMonth}
        setToMonth={setToMonth}
        fromYear={fromYear}
        setFromYear={setFromYear}
        fromWeek={fromWeek}
        setFromWeek={setFromWeek}
        toYear={toYear}
        setToYear={setToYear}
        toWeek={toWeek}
        setToWeek={setToWeek}
      />
      {/* Cumulative Nested Chart */}
      <PlasticCumulativeNested
        data={cumulativeNestedData as any}
        isLoading={isLoadingCumulativeNested}
        period={nestedPeriod}
        setPeriod={setNestedPeriod}
        year={nestedYear}
        setYear={setNestedYear}
        fromMonth={nestedFromMonth}
        setFromMonth={setNestedFromMonth}
        toMonth={nestedToMonth}
        setToMonth={setNestedToMonth}
        fromYear={nestedFromYear}
        setFromYear={setNestedFromYear}
        fromWeek={nestedFromWeek}
        setFromWeek={setNestedFromWeek}
        toYear={nestedToYear}
        setToYear={setNestedToYear}
        toWeek={nestedToWeek}
        setToWeek={setNestedToWeek}
      />
    </main>
  );
}

export default withProtectedRoute(ConversionScreen);
